import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { IconButton, Paper, Popper, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import * as React from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { messagingActions } from '../../reducers/messagingSlice';
import { selectIsPlaying } from '../../selectors/message';
import {
  selectFeatureFlagHitl3,
  selectIsHitl7Enabled,
} from '../../redux/features/config/config.selector';
import { audioPlayer } from '../../utils/asrcAudio';
import Colors from '../../utils/color';
import {
  HITL7_HEADER_ICON_BOX,
  HITL7_HEADER_ICON_INSET,
} from '../../utils/hitl3Spacing';
import { HeaderVolumeSlider } from '../MainPageLayout/HeaderVolumeSlider';

const useStyles = makeStyles((theme: Theme) => ({
  iconContainer: {
    width: '32px',
    height: '100%',
    padding: '0px !important',
    display: 'flex',
    flexDirection: 'column',
    margin: '0px !important',
    flexShrink: 0,
  },
  icon: {
    color: Colors.dim,
    width: '100% !important',
    height: '32px !important',
    padding: '4px',
    paddingRight: '4px',
    borderRadius: '4px',
  },
  active: {
    color: Colors.cerulean,
    width: '100% !important',
    height: '42px !important',
    padding: '2px',
    paddingRight: '4px',
    borderRadius: '4px',
  },
}));

interface PlayerProps {
  /**
   * HITL-1702: what the volume popper hangs from. Under 7.0 the speaker sits on
   * the top row of a block with the countdown beneath it, so a popper anchored
   * to the speaker itself would open over the countdown; the header passes the
   * whole block instead. Defaults to the speaker (legacy).
   */
  popperAnchorRef?: React.RefObject<HTMLElement>;
}

// HITL-1702: the 7.0 speaker glyph. Same fixed box as every other 7.0 header
// icon, and green when on — the colour the car / network indicators use for
// "active" — instead of legacy's cerulean, so the cluster reads as one set.
// `sx` and not the `icon`/`active` classes: those carry `!important` heights
// that an inline override cannot beat.
const hitl7IconSx = (on: boolean) => ({
  width: HITL7_HEADER_ICON_BOX,
  height: HITL7_HEADER_ICON_BOX,
  p: `${HITL7_HEADER_ICON_INSET}px`,
  borderRadius: '4px',
  color: on ? Colors.brightGreen : Colors.dim,
});

export const Player = ({ popperAnchorRef }: PlayerProps = {}) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const isPlaying = useAppSelector(selectIsPlaying);
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  // HITL-1662: the speaker button keeps its own 32px container rather than
  // going through VisualIndicator, so it has to be shrunk alongside the rest
  // for the header to hold one row at 1024px.
  const isHitl7 = useAppSelector(selectIsHitl7Enabled);
  const compact = isHitl7 ? { width: '24px' } : undefined;
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout>>();
  // Mirror of the player's mute state so the icon re-renders; the level and the
  // mute flag themselves live on the audioPlayer singleton (HITL-1472).
  const [isMuted, setIsMuted] = React.useState(audioPlayer.isMuted);

  // Playback toggling tears the AudioContext down and builds a new one. The
  // player carries the level and mute flag across that, so re-sync the icon
  // rather than letting it drift back to its initial value.
  React.useEffect(() => {
    setIsMuted(audioPlayer.isMuted);
  }, [isPlaying]);

  const toggleAudio = () => {
    dispatch(messagingActions.setIsPlaying(!isPlaying));
  };

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };
  // Volume only matters while audio is playing — reveal the slider on hover.
  const openPopper = (event: React.MouseEvent<HTMLElement>) => {
    if (!isPlaying) return;
    cancelClose();
    setAnchorEl(popperAnchorRef?.current ?? event.currentTarget);
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setAnchorEl(null), 200);
  };

  const open = Boolean(anchorEl) && isPlaying;

  // OFF (HITL 3.0 disabled): the original simple play/pause toggle. The inline
  // transcript VolumeSlider (Audio/VolumeSlider) carries volume in this mode.
  if (!isHitl3) {
    return (
      <IconButton
        onClick={() => toggleAudio()}
        className={classes.iconContainer}
        style={compact}
      >
        {
          <>
            <VolumeUpIcon
              fontSize="small"
              className={isPlaying ? classes.active : classes.icon}
            />
          </>
        }
      </IconButton>
    );
  }

  // ON (HITL 3.0): the header carries volume via a hover popover; the transcript
  // card no longer renders an inline volume control.
  return (
    <>
      <div onMouseEnter={openPopper} onMouseLeave={scheduleClose}>
        <IconButton
          onClick={toggleAudio}
          className={classes.iconContainer}
          style={compact}
        >
          {isHitl7 ? (
            isPlaying && isMuted ? (
              <VolumeOffIcon fontSize="small" sx={hitl7IconSx(false)} />
            ) : (
              <VolumeUpIcon fontSize="small" sx={hitl7IconSx(isPlaying)} />
            )
          ) : isPlaying && isMuted ? (
            // Muted while playing → show "0 sound" (off) icon.
            <VolumeOffIcon fontSize="small" className={classes.icon} />
          ) : (
            <VolumeUpIcon
              fontSize="small"
              className={isPlaying ? classes.active : classes.icon}
            />
          )}
        </IconButton>
      </div>
      {/* Popper (not Popover) — no backdrop/modal, so hover isn't interrupted. */}
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom"
        // One above the default modal zIndex (1300) so the volume popover sits
        // over surrounding chrome. Inline (not makeStyles) so the OFF Player's
        // style rules stay byte-identical to the original.
        style={{ zIndex: 1301 }}
      >
        <Paper
          elevation={3}
          // The popover portals out of the header to document.body, so tag it
          // as header chrome — otherwise dragging the volume slider counts as
          // an order interaction and trips the "take over this order?" prompt
          // (AIStatusBar excludes [data-header="true"] from that detection).
          data-header="true"
          sx={{ width: 220, py: 1, px: 1.5 }}
          onMouseEnter={cancelClose}
          // Don't close while a button is held (dragging the slider past the
          // paper edge would otherwise dismiss the popover mid-drag).
          onMouseLeave={(e) => {
            if (e.buttons === 0) scheduleClose();
          }}
        >
          <HeaderVolumeSlider isMuted={isMuted} onMutedChange={setIsMuted} />
        </Paper>
      </Popper>
    </>
  );
};
